import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNetwork } from '@mantine/hooks';
import * as actions from '@actions';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import useHeidiError from '@hooks/useHeidiError';
import { logAiNotesAction } from './heidiLogger';

export type HeidiSessionStatus = 'idle' | 'active' | 'paused' | 'processing' | 'completed';

export interface FinaliseNotes {
  hpc: string;
  plan: string;
}

export interface HeidiSessionContextValue {
  sessionId: string | null;
  status: HeidiSessionStatus;
  notes: FinaliseNotes | null;
  hasTranscriptionError: boolean;
  hasHeidiError: boolean;
  startSession: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<void>;
}

const HeidiSessionContext = createContext<HeidiSessionContextValue | null>(null);

const getStorageKey = (appointmentId: number) => `heidi-session-${appointmentId}`;

interface StoredSession {
  sessionId: string | null;
  status: HeidiSessionStatus;
}

const saveSession = (appointmentId: number, data: StoredSession) => {
  sessionStorage.setItem(getStorageKey(appointmentId), JSON.stringify(data));
};

const loadSession = (appointmentId: number): StoredSession | null => {
  const raw = sessionStorage.getItem(getStorageKey(appointmentId));
  if (!raw) return null;

  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
};

// Convert plain text line breaks to HTML structure to maintain formatting
const formatRawTextToHtml = (rawText: string): string => {
  if (!rawText) return '';
  return (
    rawText
      .split('\n\n')
      .map((section: string) =>
        section
          .split('\n')
          .map((line: string) => `<p>${line.trim()}</p>`)
          .join('')
      )
      // TipTap uses this for line breaks, and completely ignores <br>
      .join('<p></p>')
  );
};

interface HeidiSessionProviderProps {
  children: React.ReactNode;
}

export const HeidiSessionProvider = ({ children }: HeidiSessionProviderProps) => {
  const appointmentId = useSessionAppointmentId();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<HeidiSessionStatus>('idle');
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [notes, setNotes] = useState<FinaliseNotes | null>(null);
  const pausedByNetworkRef = useRef(false);

  const dispatch = useDispatch();
  const heidiEnabled = useHeidiEnabled(appointmentId);
  const { detailsCurrentAppointment } = useAppointmentDetails(appointmentId);
  const {
    ai_start_date: aiStartDate,
    ai_end_date: aiEndDate,
    ai_session_id: aiSessionId,
    ai_error_flag: aiErrorFlag
  } = detailsCurrentAppointment || {};
  const hasBackendError = !!aiErrorFlag;

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const { refetch: fetchSession } = useQuery({
    queryKey: ['heidiSession', appointmentId],
    queryFn: async () => {
      return dispatch(actions.getHeidiSession(appointmentId));
    },
    select: (data) => data?.session_id,
    enabled: false
  });

  // Initiates a Heidi AI session. Persists to sessionStorage for case review refresh recovery.
  const startSession = useCallback(async () => {
    if (statusRef.current !== 'idle') return;
    if (!heidiEnabled) return;
    if (loadSession(appointmentId)) return;

    setSessionError(null);

    const { data: newSessionId, error: queryError } = await fetchSession();

    if (queryError || !newSessionId) {
      const errorMessage = queryError instanceof Error ? queryError.message : 'Failed to start Heidi session';
      setStatus('idle');
      setSessionError(errorMessage);
      logAiNotesAction('session-start-failed', 'ERROR', {
        appointmentId,
        error: errorMessage
      });
      dispatch(actions.reportHeidiSessionError(appointmentId)).catch(() => {});
      return;
    }

    setSessionId(newSessionId);
    setStatus('active');
    logAiNotesAction('session-started', 'INFO', {
      appointmentId,
      sessionId: newSessionId
    });

    saveSession(appointmentId, { sessionId: newSessionId, status: 'active' });
  }, [appointmentId, heidiEnabled, fetchSession]);

  const pause = useCallback(() => {
    if (statusRef.current !== 'active') return;

    pausedByNetworkRef.current = false;

    logAiNotesAction('session-paused', 'INFO', {
      appointmentId,
      sessionId
    });
    setStatus('paused');
    saveSession(appointmentId, { sessionId, status: 'paused' });
  }, [appointmentId, sessionId]);

  const resume = useCallback(() => {
    if (statusRef.current !== 'paused') return;

    logAiNotesAction('session-resumed', 'INFO', {
      appointmentId,
      sessionId,
      pausedBy: pausedByNetworkRef.current ? 'network' : 'manual'
    });

    pausedByNetworkRef.current = false;

    setStatus('active');
    saveSession(appointmentId, { sessionId, status: 'active' });
  }, [appointmentId, sessionId]);

  const { isActualError } = useHeidiError();
  const hasTranscriptionError = isActualError || hasBackendError;
  const hasActivationError = (!!sessionError && status === 'idle') || hasBackendError;
  const [finalisationFailed, setFinalisationFailed] = useState(false);
  const hasFinalisationError = finalisationFailed || hasBackendError;
  const hasHeidiError = hasTranscriptionError || hasActivationError || hasFinalisationError;

  // Finalizing Heidi session
  const { mutateAsync: fetchFinalisation } = useMutation({
    mutationKey: ['heidiFinalisation', appointmentId],
    mutationFn: () => dispatch(actions.finaliseHeidiSession(appointmentId))
  });

  // Ends session. Flow: processing (audio stops, finalization POST) → completed (notes stored)
  const stop = useCallback(async (): Promise<void> => {
    if (statusRef.current === 'idle') return;

    setStatus('processing');

    logAiNotesAction('session-finalisation-started', 'INFO', {
      appointmentId,
      sessionId
    });

    saveSession(appointmentId, { sessionId, status: 'processing' });

    const handleFinalisationError = (message: string) => {
      logAiNotesAction('session-finalisation-failed', 'ERROR', {
        appointmentId,
        sessionId,
        error: message
      });

      setSessionError(message);
      setFinalisationFailed(true);
      setStatus('completed');
      saveSession(appointmentId, { sessionId, status: 'completed' });
      const savedTranscript = sessionStorage.getItem(`heidi-transcript-${appointmentId}`);
      dispatch(actions.reportHeidiSessionError(appointmentId, savedTranscript ?? null)).catch(() => {});
    };

    try {
      const finalisedNotes = await fetchFinalisation();

      if (typeof finalisedNotes?.hpc !== 'string' || typeof finalisedNotes?.plan !== 'string') {
        handleFinalisationError('Failed to finalise Heidi session');
        return;
      }

      const formattedNotes: FinaliseNotes = {
        hpc: formatRawTextToHtml(finalisedNotes.hpc),
        plan: formatRawTextToHtml(finalisedNotes.plan)
      };

      setNotes(formattedNotes);

      logAiNotesAction('session-finalisation-succeeded', 'INFO', {
        appointmentId,
        sessionId
      });

      setStatus('completed');
      saveSession(appointmentId, { sessionId, status: 'completed' });
    } catch (finaliseError) {
      const errorMessage = finaliseError instanceof Error ? finaliseError.message : 'Failed to finalise Heidi session';
      handleFinalisationError(errorMessage);
    }
  }, [appointmentId, sessionId, fetchFinalisation, dispatch]);

  // Auto-complete session when transcription error is detected
  useEffect(() => {
    if (isActualError && statusRef.current !== 'completed' && statusRef.current !== 'idle') {
      logAiNotesAction('transcription-error', 'ERROR', {
        appointmentId,
        sessionId: sessionIdRef.current
      });

      const savedTranscript = sessionStorage.getItem(`heidi-transcript-${appointmentId}`);
      dispatch(actions.reportHeidiSessionError(appointmentId, savedTranscript ?? null)).catch(() => {});
      setStatus('completed');
      saveSession(appointmentId, { sessionId, status: 'completed' });
    }
  }, [isActualError, appointmentId, sessionId, dispatch]);

  // Auto-pause on unmount so recovery restores paused state instead of auto-restarting.
  useEffect(() => {
    return () => {
      if (statusRef.current === 'active') {
        saveSession(appointmentId, { sessionId: sessionIdRef.current, status: 'paused' });
      }
    };
  }, [appointmentId]);

  const { online } = useNetwork();

  useEffect(() => {
    if (!online && statusRef.current === 'active') {
      pausedByNetworkRef.current = true;

      setStatus('paused');
      saveSession(appointmentId, { sessionId: sessionIdRef.current, status: 'paused' });
    } else if (online && statusRef.current === 'paused') {
      logAiNotesAction('session-resumed-network-online', 'INFO', {
        appointmentId,
        sessionId: sessionIdRef.current,
        pausedBy: pausedByNetworkRef.current ? 'network' : 'manual'
      });

      pausedByNetworkRef.current = false;

      setStatus('active');
      saveSession(appointmentId, { sessionId: sessionIdRef.current, status: 'active' });
    }
  }, [online, appointmentId]);

  // Session recovery on mount. Determine whether to check sessionStorage or backend as fallback
  useEffect(() => {
    if (statusRef.current !== 'idle') return;

    // sessionStorage — synchronous, instant
    const saved = loadSession(appointmentId);

    if (!saved) {
      // Backend fallback — for when sessionStorage is empty: new tab, cleared storage
      if (aiEndDate) {
        setStatus('completed');
      } else if (aiStartDate && aiSessionId && !aiEndDate && !hasBackendError) {
        // Restore session if not ended
        setStatus('paused');
        saveSession(appointmentId, { sessionId: aiSessionId, status: 'paused' });
      }
    } else {
      const restoreSession = async (savedStatus: HeidiSessionStatus, savedSessionId: string | null) => {
        let restoredId = savedSessionId;

        if (!restoredId) {
          const { data: fetchedId } = await fetchSession();
          restoredId = fetchedId ?? null;
        }

        if (restoredId) {
          setSessionId(restoredId);
        }
        setStatus(savedStatus);
        saveSession(appointmentId, { sessionId: restoredId, status: savedStatus });
      };

      switch (saved.status) {
        case 'completed':
          setStatus('completed');
          break;
        case 'processing':
        case 'paused':
        case 'active':
          restoreSession(saved.status, saved.sessionId).catch(() => {});
          break;
        default:
          startSession().catch(() => {});
          break;
      }
    }
  }, [appointmentId, startSession, fetchSession, aiEndDate, aiStartDate, aiSessionId]);

  const value = useMemo<HeidiSessionContextValue>(
    () => ({
      sessionId,
      status,
      notes,
      hasTranscriptionError,
      hasHeidiError,
      startSession,
      pause,
      resume,
      stop
    }),
    [sessionId, status, notes, hasTranscriptionError, hasHeidiError, startSession, pause, resume, stop]
  );

  return <HeidiSessionContext.Provider value={value}>{children}</HeidiSessionContext.Provider>;
};

export const useHeidiSession = (): HeidiSessionContextValue => {
  const context = useContext(HeidiSessionContext);

  if (!context) {
    throw new Error('useHeidiSession must be used within a <HeidiSessionProvider>');
  }

  return context;
};
